import type { MarketplaceProduct } from '@/types/api';

export type MarketplaceSort =
  | 'recommended'
  | 'name-asc'
  | 'price-asc'
  | 'price-desc';

export interface MarketplaceFilters {
  needs: string[];
  methods: string[];
}

export interface MarketplaceFilterOption {
  value: string;
  label: string;
  count: number;
}

export const EMPTY_MARKETPLACE_FILTERS: MarketplaceFilters = {
  needs: [],
  methods: [],
};

// Single-compound peptide ids (BPC-157, TB-500, Semax, KPV, Epitalon, MOTS-c).
// Their copy can mention metabolism, but none is a weight-loss treatment. Only
// the product's own id excludes it: other products' copy may name a peptide.
const SINGLE_PEPTIDE_ID = /^(?:bpc157|tb500|semax|kpv|epitalon|motsc)-/;

const NEED_DEFINITIONS: Array<{
  value: string;
  label: string;
  pattern: RegExp;
  excludeId?: RegExp;
}> = [
  {
    value: 'weight-management',
    label: 'Weight management',
    pattern: /tirzepatide|semaglutide|weight loss|fat loss|metaboli/,
    excludeId: SINGLE_PEPTIDE_ID,
  },
  {
    value: 'energy-focus',
    label: 'Energy & focus',
    pattern:
      /\bnad\b|mic\s*b12|energy|focus|cognitive|fatigue|\bsemax\b|\bmots ?c\b/,
  },
  {
    value: 'recovery-performance',
    label: 'Recovery & performance',
    pattern:
      /glutathione|sermorelin|recovery|muscle|performance|detox|\bbpc ?157\b|\btb ?500\b/,
  },
  {
    value: 'hormone-support',
    label: 'Hormone support',
    pattern: /enclomiphene|oxytocin|sermorelin|hormone|testosterone/,
  },
  {
    value: 'healthy-aging',
    label: 'Healthy aging',
    pattern:
      /\bnad\b|glutathione|ghk|healthy aging|longevity|renewal|\bepith?alon\b|\bmots ?c\b/,
  },
  {
    value: 'skin-hair',
    label: 'Skin & hair',
    pattern: /ghk|skin|hair|scalp|\bkpv\b/,
  },
  {
    value: 'sexual-health',
    label: 'Sexual health',
    pattern: /\btadalafil\b|\bpt\s*141\b|\bbremelanotide\b|\boxytocin\b/,
  },
];

const METHOD_ORDER = [
  'injectable',
  'nasal-spray',
  'oral-sublingual',
  'topical',
  'other',
] as const;

const METHOD_LABELS: Record<(typeof METHOD_ORDER)[number], string> = {
  injectable: 'Injectable',
  'nasal-spray': 'Nasal spray',
  'oral-sublingual': 'Oral / sublingual',
  topical: 'Topical',
  other: 'Other',
};

function productText(product: MarketplaceProduct): string {
  return normalizeText(
    [
      product.title,
      product.subtitle,
      product.summary,
      product.description,
      product.treatment_support,
      product.quantity,
      product.method,
      product.canonical_product_id,
      ...(product.tags ?? []),
      ...(product.value_props ?? []),
    ]
      .filter(Boolean)
      .join(' '),
  );
}

function normalizeText(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

export function marketplaceNeedsForProduct(
  product: MarketplaceProduct,
): string[] {
  const text = productText(product);
  const id = (product.canonical_product_id ?? '').toLowerCase();
  return NEED_DEFINITIONS.filter(
    ({ pattern, excludeId }) => pattern.test(text) && !excludeId?.test(id),
  ).map(({ value }) => value);
}

export function marketplaceMethodForProduct(
  product: MarketplaceProduct,
): (typeof METHOD_ORDER)[number] {
  const text = productText(product);

  if (/inject|injectable|tirzepatide|semaglutide|glp 1|mic b12/.test(text)) {
    return 'injectable';
  }
  if (/nasal|intranasal|spray/.test(text)) return 'nasal-spray';
  if (/topical|cream|foam|gel|scalp/.test(text)) return 'topical';
  if (/oral|sublingual|tablet|capsule|troche|lozenge/.test(text)) {
    return 'oral-sublingual';
  }
  return 'other';
}

export function getMarketplaceFilterOptions(products: MarketplaceProduct[]): {
  needs: MarketplaceFilterOption[];
  methods: MarketplaceFilterOption[];
} {
  const needCounts = new Map<string, number>();
  const methodCounts = new Map<string, number>();

  for (const product of products) {
    for (const need of marketplaceNeedsForProduct(product)) {
      needCounts.set(need, (needCounts.get(need) ?? 0) + 1);
    }
    const method = marketplaceMethodForProduct(product);
    methodCounts.set(method, (methodCounts.get(method) ?? 0) + 1);
  }

  return {
    needs: NEED_DEFINITIONS.map(({ value, label }) => ({
      value,
      label,
      count: needCounts.get(value) ?? 0,
    })).filter(({ count }) => count > 0),
    methods: METHOD_ORDER.map((value) => ({
      value,
      label: METHOD_LABELS[value],
      count: methodCounts.get(value) ?? 0,
    })).filter(({ count }) => count > 0),
  };
}

export function filterAndSortMarketplaceProducts(
  products: MarketplaceProduct[],
  query: string,
  filters: MarketplaceFilters,
  sort: MarketplaceSort,
): MarketplaceProduct[] {
  const queryTokens = normalizeText(query).split(' ').filter(Boolean);

  const filtered = products.filter((product) => {
    const text = productText(product);
    const matchesQuery = queryTokens.every((token) => text.includes(token));
    const productNeeds = marketplaceNeedsForProduct(product);
    const matchesNeeds =
      filters.needs.length === 0 ||
      filters.needs.some((need) => productNeeds.includes(need));
    const matchesMethods =
      filters.methods.length === 0 ||
      filters.methods.includes(marketplaceMethodForProduct(product));

    return matchesQuery && matchesNeeds && matchesMethods;
  });

  if (sort === 'recommended') return filtered;

  return [...filtered].sort((a, b) => {
    if (sort === 'name-asc') return a.title.localeCompare(b.title);

    const aPrice = a.price_cents && a.price_cents > 0 ? a.price_cents : null;
    const bPrice = b.price_cents && b.price_cents > 0 ? b.price_cents : null;
    if (aPrice == null && bPrice == null) return a.title.localeCompare(b.title);
    if (aPrice == null) return 1;
    if (bPrice == null) return -1;
    const priceDifference =
      sort === 'price-asc' ? aPrice - bPrice : bPrice - aPrice;

    return priceDifference || a.title.localeCompare(b.title);
  });
}

export function marketplaceFilterCount(filters: MarketplaceFilters): number {
  return filters.needs.length + filters.methods.length;
}

export function toggleMarketplaceFilter(
  values: string[],
  value: string,
): string[] {
  return values.includes(value)
    ? values.filter((candidate) => candidate !== value)
    : [...values, value];
}
